import { useAuth } from "@/contexts/AuthContext";
import { getDashboardMetrics, getAdminOrders, getAdminInvoices } from "@/services/adminService";
import type { ErpOrder, ErpInvoice } from "@/types/admin";
import { demoAdminRevenueSeries } from "@/mocks/admin";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import AdminStatCard from "@/pages/portal/admin/components/AdminStatCard";
import RecentErpActivity from "@/pages/portal/admin/components/RecentErpActivity";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const BAR_COLORS = [
  "oklch(0.55 0.17 55)",
  "oklch(0.58 0.15 150)",
  "oklch(0.54 0.14 25)",
  "oklch(0.56 0.13 300)",
  "oklch(0.60 0.16 100)",
  "oklch(0.52 0.15 200)",
];

const fmt = (n: number) => n.toLocaleString("en-IN");
const fmtDate = (d: string) => {
  const dt = new Date(d);
  return `${dt.getDate()} ${dt.toLocaleString("en-IN", { month: "short" })}`;
};

export default function DashboardOverview() {
  const { accessToken } = useAuth();
  const { data, loading, error, reload } = useAdminResource(
    () => getDashboardMetrics(accessToken),
    {
      totals: {
        totalBusinesses: 0, newBusinesses: 0, businessViews: 0, totalLeads: 0, newLeads: 0,
        totalReviews: 0, pendingReviews: 0, totalUsers: 0, newUsers: 0, owners: 0,
        pendingApprovals: 0, adImpressions: 0, adClicks: 0, featuredListings: 0,
        activeSubscriptions: 0, totalRevenue: 0,
      },
      trend: [],
      byCategory: [],
      byDistrict: [],
      topBusinesses: [],
    },
    [accessToken],
  );

  const { data: orders, loading: ordersLoading, error: ordersError, reload: ordersReload } = useAdminResource(
    () => getAdminOrders(accessToken),
    [] as ErpOrder[],
    [accessToken],
  );

  const { data: invoices, loading: invoicesLoading, error: invoicesError, reload: invoicesReload } = useAdminResource(
    () => getAdminInvoices(accessToken),
    [] as ErpInvoice[],
    [accessToken],
  );

  if (!loading && (error || ordersError || invoicesError)) {
    return <AdminDataError message={error || ordersError || invoicesError} onRetry={() => { reload(); ordersReload(); invoicesReload(); }} />;
  }

  const t = data.totals ?? {
    totalBusinesses: 0, newBusinesses: 0, businessViews: 0, totalLeads: 0, newLeads: 0,
    totalReviews: 0, pendingReviews: 0, totalUsers: 0, newUsers: 0, owners: 0,
    pendingApprovals: 0, adImpressions: 0, adClicks: 0, featuredListings: 0,
    activeSubscriptions: 0, totalRevenue: 0,
  };
  const ctr = t.adImpressions ? ((t.adClicks / t.adImpressions) * 100).toFixed(2) : "0.00";

  const ordersByStatus = (["pending", "paid", "processing", "fulfilled", "refunded", "cancelled"] as const).map((s) => ({
    name: s,
    count: orders.filter((o) => o.status === s).length,
  }));
  const orderValue = orders
    .filter((o) => o.status === "paid" || o.status === "fulfilled")
    .reduce((s, o) => s + o.amount, 0);
  const avgOrder = orders.length ? Math.round(orderValue / Math.max(1, orders.filter((o) => o.amount > 0).length)) : 0;

  const primaryStats = [
    { label: "Total Businesses",       value: loading ? "—" : fmt(t.totalBusinesses),   icon: "ri-store-2-line",           accent: "primary"   as const, sub: `${t.newBusinesses} new this month` },
    { label: "Business Views",         value: loading ? "—" : fmt(t.businessViews),     icon: "ri-eye-line",               accent: "accent"    as const, sub: "across all listings" },
    { label: "Total Leads",            value: loading ? "—" : fmt(t.totalLeads),        icon: "ri-mail-open-line",         accent: "secondary" as const, sub: `${t.newLeads} unread` },
    { label: "Reviews",                value: loading ? "—" : fmt(t.totalReviews),      icon: "ri-chat-3-line",            accent: "amber"     as const, sub: `${t.pendingReviews} awaiting review` },
    { label: "Users",                  value: loading ? "—" : fmt(t.totalUsers),        icon: "ri-group-line",             accent: "emerald"   as const, sub: `${t.owners} business owners` },
    { label: "Featured Listings",      value: loading ? "—" : fmt(t.featuredListings),  icon: "ri-star-line",              accent: "primary"   as const, sub: "currently promoted" },
    { label: "Ad Impressions",         value: loading ? "—" : fmt(t.adImpressions),     icon: "ri-eye-2-line",             accent: "accent"    as const, sub: "all placements" },
    { label: "Ad Clicks",              value: loading ? "—" : fmt(t.adClicks),          icon: "ri-cursor-line",            accent: "secondary" as const, sub: `${ctr}% CTR` },
    { label: "Total Revenue",          value: loading ? "—" : `₹${fmt(t.totalRevenue)}`, icon: "ri-money-rupee-circle-line", accent: "emerald"   as const, sub: `${t.activeSubscriptions} active plans` },
  ];

  return (
    <div className="space-y-8">
      {/* Required overview metrics */}
      <div>
        <h2 className="mb-4 font-heading text-base font-bold text-foreground-950">Platform Overview</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {primaryStats.map((s) => (
            <AdminStatCard key={s.label} {...s} />
          ))}
        </div>
      </div>

      {/* Secondary counters */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          { label: "New Businesses",  value: t.newBusinesses,     icon: "ri-add-circle-line",    accent: "primary"   as const },
          { label: "Pending Approvals", value: t.pendingApprovals, icon: "ri-time-line",          accent: "amber"     as const },
          { label: "Pending Reviews", value: t.pendingReviews,    icon: "ri-star-half-line",     accent: "rose"      as const },
          { label: "New Users",       value: t.newUsers,          icon: "ri-user-add-line",      accent: "emerald"   as const },
        ].map((s) => (
          <AdminStatCard key={s.label} {...s} value={loading ? "—" : fmt(s.value)} />
        ))}
      </div>

      {/* Trend chart */}
      <div className="rounded-xl border border-background-200 bg-background-50 p-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-sm font-bold text-foreground-950">14-Day Activity</h3>
            <p className="mt-0.5 text-xs text-foreground-500">Leads, profile views and new signups per day.</p>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <span className="flex items-center gap-1.5 text-foreground-600">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-primary-500" /> Leads
            </span>
            <span className="flex items-center gap-1.5 text-foreground-600">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-accent-500" /> Views
            </span>
            <span className="flex items-center gap-1.5 text-foreground-600">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-secondary-500" /> Signups
            </span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={data.trend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gLeads" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="oklch(0.55 0.17 55)" stopOpacity={0.3} />
                <stop offset="100%" stopColor="oklch(0.55 0.17 55)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="oklch(0.58 0.15 150)" stopOpacity={0.25} />
                <stop offset="100%" stopColor="oklch(0.58 0.15 150)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-background-200, #e5e7eb)" />
            <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 10, fill: "var(--color-foreground-500, #6b7280)" }} tickLine={false} axisLine={false} interval={1} />
            <YAxis tick={{ fontSize: 10, fill: "var(--color-foreground-500, #6b7280)" }} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }} labelFormatter={fmtDate} />
            <Area type="monotone" dataKey="views" stroke="oklch(0.58 0.15 150)" strokeWidth={2} fill="url(#gViews)" />
            <Area type="monotone" dataKey="leads" stroke="oklch(0.55 0.17 55)" strokeWidth={2.5} fill="url(#gLeads)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Revenue & growth */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="rounded-xl border border-background-200 bg-background-50 p-6 xl:col-span-2">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-heading text-sm font-bold text-foreground-950">Revenue (12 months)</h3>
              <p className="mt-0.5 text-xs text-foreground-500">Monthly gross income across all listing plans.</p>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
              <i className="ri-arrow-up-line" /> {loading ? "—" : `₹${fmt(t.totalRevenue)} this year`}
            </span>
          </div>
          <ResponsiveContainer width="100%" height={230}>
            <AreaChart data={demoAdminRevenueSeries} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="gDashRev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="oklch(0.42 0.12 265)" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="oklch(0.42 0.12 265)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-background-200, #e5e7eb)" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "var(--color-foreground-500, #6b7280)" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "var(--color-foreground-500, #6b7280)" }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }} formatter={(v: number) => `₹${fmt(v)}`} />
              <Area type="monotone" dataKey="revenue" stroke="oklch(0.42 0.12 265)" strokeWidth={2.5} fill="url(#gDashRev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="flex flex-col gap-4">
          <AdminStatCard label="Orders Collected" value={loading || ordersLoading ? "—" : `₹${fmt(orderValue)}`} icon="ri-shopping-bag-3-line" accent="emerald" sub="paid + fulfilled" />
          <AdminStatCard label="Average Order" value={loading || ordersLoading ? "—" : `₹${fmt(avgOrder)}`} icon="ri-price-tag-3-line" accent="primary" sub={`${orders.length} orders total`} />
          <AdminStatCard label="Active Plans" value={loading ? "—" : fmt(t.activeSubscriptions)} icon="ri-refresh-line" accent="accent" sub="recurring subscriptions" />
        </div>
      </div>

      {/* Recent orders & invoices */}
      <RecentErpActivity orders={orders} invoices={invoices} loading={ordersLoading || invoicesLoading} />

      {/* Orders by status */}
      <div className="rounded-xl border border-background-200 bg-background-50 p-6">
        <h3 className="mb-1 font-heading text-sm font-bold text-foreground-950">Order Pipeline</h3>
        <p className="mb-5 text-xs text-foreground-500">Listing-plan orders grouped by fulfilment status.</p>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={ordersByStatus} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-background-200, #e5e7eb)" />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: "var(--color-foreground-600, #4b5563)" }} tickLine={false} axisLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--color-foreground-500, #6b7280)" }} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }} formatter={(v: number) => [`${v} orders`, "Orders"]} />
            <Bar dataKey="count" radius={[4, 4, 0, 0]} fill="oklch(0.582 0.092 175)" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Category + top businesses */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-xl border border-background-200 bg-background-50 p-6">
          <h3 className="mb-1 font-heading text-sm font-bold text-foreground-950">Listings by Category</h3>
          <p className="mb-5 text-xs text-foreground-500">Active businesses per directory category.</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.byCategory} margin={{ top: 4, right: 8, left: -20, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-background-200, #e5e7eb)" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "var(--color-foreground-600, #4b5563)" }} tickLine={false} axisLine={false} angle={-30} textAnchor="end" interval={0} tickFormatter={(v: string) => v.split(" & ")[0].slice(0, 14)} />
              <YAxis tick={{ fontSize: 10, fill: "var(--color-foreground-500, #6b7280)" }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }} formatter={(v: number) => [`${v} listings`, "Businesses"]} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {data.byCategory.map((_e, i) => (
                  <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-background-200 bg-background-50">
          <div className="border-b border-background-200 px-6 py-4">
            <h3 className="font-heading text-sm font-bold text-foreground-950">Top Performing Listings</h3>
            <p className="mt-0.5 text-xs text-foreground-500">Ranked by total profile views.</p>
          </div>
          <div className="divide-y divide-background-100">
            {data.topBusinesses.map((b, i) => (
              <div key={b.slug} className="flex items-center gap-4 px-6 py-3.5">
                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${i === 0 ? "bg-accent-100 text-accent-900" : "bg-background-100 text-foreground-600"}`}>
                  {i + 1}
                </span>
                <a href={`/business/${b.slug}`} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1 truncate text-xs font-semibold text-foreground-900 hover:text-primary-700 hover:underline">
                  {b.name}
                </a>
                <span className="shrink-0 text-sm font-bold tabular-nums text-foreground-950">{fmt(b.views)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}